import { useEffect, useRef, useCallback } from 'react';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import OLFeature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { fromLonLat } from 'ol/proj';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style';
import type OLMap from 'ol/Map';
import { useAppSelector } from '../store/hooks';
import {
  selectIsEditingGeometry,
  selectEditingFeatureId,
  selectEditingLayerId,
  selectSelectedNodeIndex,
} from '../store/slices/geometryEditingSlice';
import { getNodes, type Geometry, type Coordinate } from '../services/GeometryEditor';

/**
 * Configuration for vertex handle styling.
 */
export interface NodeOverlayStyleConfig {
  /** Radius of normal vertex handles in pixels. Default: 8 */
  radius?: number;
  /** Fill color for normal vertex handles. Default: '#2196f3' (blue) */
  fillColor?: string;
  /** Stroke color for normal vertex handles. Default: '#ffffff' (white) */
  strokeColor?: string;
  /** Stroke width for normal vertex handles. Default: 2 */
  strokeWidth?: number;
  /** Radius of the selected vertex handle in pixels. Default: 10 */
  selectedRadius?: number;
  /** Fill color for the selected vertex handle. Default: '#ff5722' (orange-red) */
  selectedFillColor?: string;
  /** Stroke color for the selected vertex handle. Default: '#ffffff' (white) */
  selectedStrokeColor?: string;
  /** Stroke width for the selected vertex handle. Default: 3 */
  selectedStrokeWidth?: number;
}

const DEFAULT_STYLE_CONFIG: Required<NodeOverlayStyleConfig> = {
  radius: 8,
  fillColor: '#2196f3',
  strokeColor: '#ffffff',
  strokeWidth: 2,
  selectedRadius: 10,
  selectedFillColor: '#ff5722',
  selectedStrokeColor: '#ffffff',
  selectedStrokeWidth: 3,
};

/**
 * Options for the useNodeEditingOverlay hook.
 */
export interface UseNodeEditingOverlayOptions {
  /** Live map value; preferred so restored editing binds after map initialization. */
  map?: OLMap | null;
  /** Backward-compatible map reference for existing consumers. */
  mapRef?: React.RefObject<OLMap | null>;
  /** Optional style configuration for vertex handles */
  styleConfig?: NodeOverlayStyleConfig;
}

/**
 * Creates the OL Style for a normal (unselected) vertex handle.
 */
export function createNodeStyle(config: Required<NodeOverlayStyleConfig>): Style {
  return new Style({
    image: new CircleStyle({
      radius: config.radius,
      fill: new Fill({ color: config.fillColor }),
      stroke: new Stroke({
        color: config.strokeColor,
        width: config.strokeWidth,
      }),
    }),
  });
}

/**
 * Creates the OL Style for the currently selected vertex handle.
 */
export function createSelectedNodeStyle(config: Required<NodeOverlayStyleConfig>): Style {
  return new Style({
    image: new CircleStyle({
      radius: config.selectedRadius,
      fill: new Fill({ color: config.selectedFillColor }),
      stroke: new Stroke({
        color: config.selectedStrokeColor,
        width: config.selectedStrokeWidth,
      }),
    }),
  });
}

/**
 * Builds an array of OL Point features from vertex coordinates.
 * Each feature stores its node index as a property for identification.
 *
 * @param nodes - Array of [lon, lat] coordinates from getNodes()
 * @returns Array of OL Features with Point geometries
 */
export function buildNodeFeatures(nodes: Coordinate[]): OLFeature<Point>[] {
  return nodes.map((coord, index) => {
    const feature = new OLFeature<Point>({
      geometry: new Point(fromLonLat(coord)),
    });
    feature.set('nodeIndex', index);
    feature.setId(`node-${index}`);
    return feature;
  });
}

/**
 * useNodeEditingOverlay
 *
 * A React hook that manages an OpenLayers VectorLayer overlay displaying
 * editable vertex handles when geometry node editing mode is active.
 *
 * The overlay:
 * - Appears when `isEditingGeometry` is true in Redux state
 * - Displays all vertices of the editing feature as small circle handles
 * - Highlights the currently selected node with a distinct style
 * - Disappears when node editing mode is deactivated
 * - Updates when the geometry changes (e.g., after a node move)
 *
 * **Validates: Requirement 16.1** – WHEN a Registered_User selects a Feature
 * and activates node editing mode, THE Geometry_Editor SHALL display all nodes
 * as editable points.
 */
export function useNodeEditingOverlay({
  map,
  mapRef,
  styleConfig,
}: UseNodeEditingOverlayOptions): void {
  const isEditing = useAppSelector(selectIsEditingGeometry);
  const editingFeatureId = useAppSelector(selectEditingFeatureId);
  const editingLayerId = useAppSelector(selectEditingLayerId);
  const selectedNodeIndex = useAppSelector(selectSelectedNodeIndex);
  const liveMap = map ?? mapRef?.current ?? null;

  // Access the layers state to find the feature geometry
  const layers = useAppSelector((state) => state.layers.layers);

  // Refs for the overlay layer and source
  const overlayLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const overlaySourceRef = useRef<VectorSource | null>(null);

  // Merge user config with defaults
  const config: Required<NodeOverlayStyleConfig> = {
    ...DEFAULT_STYLE_CONFIG,
    ...styleConfig,
  };

  // Memoize style creation (styles don't change unless config changes)
  const normalStyle = useRef<Style>(createNodeStyle(config));
  const selectedStyle = useRef<Style>(createSelectedNodeStyle(config));

  // Update styles when config changes
  useEffect(() => {
    normalStyle.current = createNodeStyle(config);
    selectedStyle.current = createSelectedNodeStyle(config);
  }, [
    config.radius,
    config.fillColor,
    config.strokeColor,
    config.strokeWidth,
    config.selectedRadius,
    config.selectedFillColor,
    config.selectedStrokeColor,
    config.selectedStrokeWidth,
  ]);

  /**
   * Find the geometry of the feature being edited from the layers state.
   */
  const getEditingGeometry = useCallback((): Geometry | null => {
    if (!editingFeatureId || !editingLayerId) return null;

    const layer = layers.find((l) => l.id === editingLayerId);
    if (!layer || !layer.features) return null;

    const feature = layer.features.find((f) => f.id === editingFeatureId);
    if (!feature || !feature.geometry) return null;

    return feature.geometry as unknown as Geometry;
  }, [editingFeatureId, editingLayerId, layers]);

  // Remove the overlay from the exact map that owns it on map replacement/unmount.
  useEffect(() => {
    return () => {
      if (liveMap && overlayLayerRef.current) {
        liveMap.removeLayer(overlayLayerRef.current);
        overlayLayerRef.current = null;
        overlaySourceRef.current = null;
      }
    };
  }, [liveMap]);

  /**
   * Main effect: create/update the overlay while node editing owns the live map.
   */
  useEffect(() => {
    if (!liveMap) return;

    if (!isEditing) {
      if (overlayLayerRef.current) {
        liveMap.removeLayer(overlayLayerRef.current);
        overlayLayerRef.current = null;
        overlaySourceRef.current = null;
      }
      return;
    }

    const geometry = getEditingGeometry();
    if (!geometry) {
      if (overlayLayerRef.current) {
        liveMap.removeLayer(overlayLayerRef.current);
        overlayLayerRef.current = null;
        overlaySourceRef.current = null;
      }
      return;
    }

    const nodeFeatures = buildNodeFeatures(getNodes(geometry));
    nodeFeatures.forEach((feature) => {
      const nodeIdx = feature.get('nodeIndex') as number;
      feature.setStyle(nodeIdx === selectedNodeIndex ? selectedStyle.current : normalStyle.current);
    });

    if (!overlaySourceRef.current) {
      overlaySourceRef.current = new VectorSource();
    }
    overlaySourceRef.current.clear();
    overlaySourceRef.current.addFeatures(nodeFeatures);

    if (!overlayLayerRef.current) {
      overlayLayerRef.current = new VectorLayer({
        source: overlaySourceRef.current,
        zIndex: 9999,
      });
      (overlayLayerRef.current as any).isNodeEditingOverlay = true;
      liveMap.addLayer(overlayLayerRef.current);
    }
  }, [
    liveMap,
    isEditing,
    editingFeatureId,
    editingLayerId,
    selectedNodeIndex,
    layers,
    getEditingGeometry,
  ]);
}

export default useNodeEditingOverlay;
